import React, { useRef, useMemo, useEffect } from 'react';

import { FormattedMessage, FormattedHTMLMessage } from 'react-intl';
import styled from 'styled-components';
import Validate from 'validate.js';

import AuthService from '../../../../../../services/Api/Auth';
import Url from '../../../../../../services/Url';

import connectAuth, { IAuthMapStateToProps, IConnectedProps } from '../../hooks/connectAuth';

import Button from '../../../Common/Form/Button';
import Input from '../../../Common/Form/Input/Input';
import LoadingSvg from '../../../Common/LoadingSvg';
import { Types, initialState as reducerInitialState } from '../../Store/LoginReducer';
import { IAuthState } from '../../Store/Reducers';

interface IForgotPassword {
    email: string,
    error: string,
    success: string,
    loading: boolean,
    glEmail?: string,
    btnDisabled: boolean,
    forgotPassword: boolean,
    showFacebookMessage: boolean
}

type TForgotPassword = IConnectedProps<IForgotPassword>;

interface IFeedbackRequest {
    success: string
}

const initialState = reducerInitialState.Login.forgotPasswordState;

const WrapperModal = styled.div`
    top: 0;
    left: 0;
    width: 100%;
    z-index: 999;
    height: 100vh;
    display: none;
    position: fixed;
    background-color: rgba(0, 0, 0, .7);
`;

const ModalContainer = styled.div`
    top: 50%;
    left: 50%;
    z-index: 1000;
    display: block;
    max-width: 100%;
    position: absolute;
    box-sizing: border-box;
    transition: transform .3s ease-out;
    transform: scale(0) translate(-50%, -50%);
`;

const ForgotModal = styled.div<{$showFacebookMessage: boolean}>`
    display: flex;
    width: 21.25rem;
    position: relative;
    align-items: center;
    min-height: 18.5rem;
    box-sizing: border-box;
    flex-direction: column;
    justify-content: center;
    gap: ${({ theme }) => theme.space.bee};
    border-radius: ${({ theme }) => theme.radii[1]};
    background-color: ${({ theme }) => theme.colors.white};
    box-shadow: 0 0 0.1875rem ${({ theme }) => theme.colors.black};
    padding: ${({ theme, $showFacebookMessage }) => `${$showFacebookMessage ? '50px' : '0'} ${theme.space.goat}`};

    > h2,
    > span {
        width: 100%;
        text-align: center;
        color: ${({ theme }) => theme.colors.gray900};
    }

    > h2 {
        font-size: ${({ theme, $showFacebookMessage }) => $showFacebookMessage ? theme.fontSizes.cat : theme.fontSizes.goat};

        font-weight: ${({ theme }) => theme.fontWeights.bold};
    }

    > span {
        font-size: ${({ theme, $showFacebookMessage }) => $showFacebookMessage ? theme.fontSizes.butterfly : theme.fontSizes.mouse};
    }
`;

const CloseDark = styled.img.attrs(() => ({ src: Url.image('close-dark.svg') }))`
    opacity: .5;
    top: 0.625rem;
    width: 1.25rem;
    right: 0.625rem;
    height: 1.25rem;
    cursor: pointer;
    position: absolute;
    object-fit: contain;
`;

const FeedbackRequest = styled.b<IFeedbackRequest>`
    width: 100%;
    border-left: 0;
    border-right: 0;
    text-align: center;
    font-size: 0.875rem;
    padding: 0.3125rem 0;
    margin-bottom: 0.4375rem;
    color: ${({ theme, success }) => success ? theme.colors.success : theme.colors.error};
    border: 0.0625rem solid ${({ theme, success }) => success ? theme.colors.success : theme.colors.error};
`;

const ForgotPassword: React.FC<TForgotPassword> = ({ intl, dispatch, ...props }) => {
    const wrapperModal = useRef<HTMLDivElement>(null);

    const {
        email,
        error,
        success,
        loading,
        glEmail,
        btnDisabled,
        forgotPassword,
        showFacebookMessage
    } = props;

    const $AuthService = new AuthService();

    const contactUrl = intl.formatMessage({ id: 'common.routes.contact' });

    const LANGS = useMemo(() => showFacebookMessage
        ? {
            title: 'auth.forgotPasswordFacebook.title',
            button: 'auth.forgotPasswordFacebook.button',
            subtitle: 'auth.forgotPasswordFacebook.description'
        } : {
            title: 'authFunctional.forgotPassword.label',
            button: 'authFunctional.forgotPassword.recovery',
            subtitle: 'authFunctional.forgotPassword.subTitle'
        }, [showFacebookMessage]);

    const handleChangeEmail = ({ target }: React.ChangeEvent<HTMLInputElement>) => dispatch({
        type: Types.CHANGE_FORGOT_PASSWORD_STATE,
        data: {
            error: '',
            success: '',
            email: target.value
        }
    });

    const handleCloseModal = () => dispatch({
        type: Types.FORGOT_PASSWORD,
        data: {
            open: false,
            showFacebookMessage: false
        }
    });

    const handleClickButton = async () => {
        dispatch({
            type: Types.CHANGE_FORGOT_PASSWORD_STATE,
            data: {
                error: '',
                success: '',
                loading: true
            }
        });

        const rules = {
            presence: {
                message: intl.formatMessage({ id: 'authFunctional.errors.invalidEmail' })
            },

            email: {
                message: intl.formatMessage({ id: 'authFunctional.errors.invalidEmail' })
            }
        };

        const isInvalid = Validate.single(email.toLowerCase(), rules);
        if (isInvalid) {
            dispatch({
                type: Types.CHANGE_FORGOT_PASSWORD_STATE,
                data: {
                    loading: false,
                    error: intl.formatMessage({ id: isInvalid[0] })
                }
            });
        }

        try {
            await $AuthService.recoveryPassword(email.toLowerCase());
            dispatch({
                type: Types.CHANGE_FORGOT_PASSWORD_STATE,
                data: {
                    error: '',
                    loading: false,
                    btnDisabled: true,
                    success: intl.formatMessage({ id: 'authFunctional.forgotPassword.successMessage' })
                }
            });
        } catch (errorResponse) {
            dispatch({
                type: Types.CHANGE_FORGOT_PASSWORD_STATE,
                data: {
                    success: '',
                    loading: false,
                    error: intl.formatMessage({ id: 'authFunctional.forgotPassword.errorMessage' })
                }
            });
        }
    };

    useEffect(() => {
        const { current } = wrapperModal;

        if (!current || (!current.style.display && !forgotPassword)) return;

        const firstElementChild = (current.firstElementChild as HTMLDivElement);

        if (!firstElementChild) return;

        if (forgotPassword) {
            current.style.display = 'block';

            setTimeout(() => {
                if (!firstElementChild) return;

                firstElementChild.style.transform = 'scale(1) translate(-50%, -50%)';
            }, 100);

            return;
        }

        firstElementChild.style.transform = 'scale(0,0) translate(-50%, -50%)';
        setTimeout(() => {
            current.style.display = 'none';

            dispatch({
                type: Types.CHANGE_FORGOT_PASSWORD_STATE,
                data: initialState
            });
        }, 280);
    }, [dispatch, forgotPassword]);

    useEffect(() => {
        if (forgotPassword) {
            document.body.style.overflow = 'hidden';
        }

        document.body.style.overflow = 'auto';
    }, [forgotPassword]);

    useEffect(() => {
        if (!glEmail) return;

        dispatch({
            type: Types.CHANGE_FORGOT_PASSWORD_STATE,
            data: { email: glEmail }
        });
    }, [dispatch, glEmail]);

    return (
        <WrapperModal ref={wrapperModal} className="auth-functional__forgot-password" role="dialog" data-open={forgotPassword}>
            <ModalContainer>
                <ForgotModal $showFacebookMessage={showFacebookMessage}>
                    <CloseDark onClick={handleCloseModal} />

                    <h2><FormattedMessage id={LANGS.title} /></h2>
                    <FormattedMessage id={LANGS.subtitle} />

                    { (success || error) && <FeedbackRequest success={success}>{(success || error)}</FeedbackRequest> }

                    <Input
                        value={email}
                        id="recover-password"
                        onChange={handleChangeEmail}
                        placeholder={intl.formatMessage({ id: 'authFunctional.personalData.placeholders.email' })}
                    />

                    <Button
                        id="recover-password"
                        disabled={btnDisabled}
                        onClick={handleClickButton}
                    >
                        { loading ? <LoadingSvg /> : <FormattedMessage id={LANGS.button} />}
                    </Button>

                    {showFacebookMessage && (
                        <FormattedHTMLMessage
                            id="auth.forgotPasswordFacebook.footer"
                            values={{ contactUrl }}
                        />
                    )}
                </ForgotModal>
            </ModalContainer>
        </WrapperModal>
    );
};

const mapStateToProps: IAuthMapStateToProps<IForgotPassword> = (state, isComparison = false) => ((!isComparison)
    ? {
        forgotPassword: (state as IAuthState).Login.forgotPassword,
        email: (state as IAuthState).Login.forgotPasswordState.email,
        error: (state as IAuthState).Login.forgotPasswordState.error,
        glEmail: (state as IAuthState).SignUp.personalData.email.value,
        success: (state as IAuthState).Login.forgotPasswordState.success,
        loading: (state as IAuthState).Login.forgotPasswordState.loading,
        btnDisabled: (state as IAuthState).Login.forgotPasswordState.btnDisabled,
        showFacebookMessage: (state as IAuthState).Login.forgotPasswordState.showFacebookMessage
    } :
    {
        email: state.email,
        error: state.error,
        success: state.success,
        loading: state.loading,
        glEmail: state.glEmail,
        btnDisabled: state.btnDisabled,
        forgotPassword: state.forgotPassword,
        showFacebookMessage: state.showFacebookMessage
    }
);

export default connectAuth(mapStateToProps)(ForgotPassword);
